import React from 'react';
import {
  Box, Card, CardContent, Typography, Chip, Button, IconButton,
  Divider, Paper, Stack, useTheme, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow,
} from '@mui/material';
import { ArrowBack, Edit } from '@mui/icons-material';
import { useDocument } from '../../hooks/useDocument';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import EmptyState from '../../components/ui/EmptyState';

const JournalEntryDetail = ({ entryId, onEdit, onBack }) => {
  const theme = useTheme();
  const { document: entry, loading } = useDocument('journalEntries', entryId);

  if (loading) return <LoadingSpinner />;
  if (!entry) return <EmptyState title="Asiento no encontrado" />;

  const totals = entry.lines?.reduce(
    (acc, line) => ({
      debit: acc.debit + (line.debit || 0),
      credit: acc.credit + (line.credit || 0),
    }),
    { debit: 0, credit: 0 }
  ) || { debit: 0, credit: 0 };

  return (
    <Paper elevation={0} sx={{ p: { xs: 2, sm: 3, md: 4 }, borderRadius: { xs: "16px", sm: "24px" }, border: "1px solid", borderColor: theme.palette.divider }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 4, flexWrap: "wrap" }}>
        <IconButton onClick={onBack} size="small"><ArrowBack /></IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={800} color="primary">
            Asiento {entry.entryNumber || 'SN'}
          </Typography>
          <Stack direction="row" spacing={1} mt={1}>
            <Chip label={entry.isManual ? 'Manual' : 'Automático'} size="small" color={entry.isManual ? 'primary' : 'default'} variant="outlined" />
            <Chip label={entry.referenceType || 'Sin referencia'} size="small" variant="outlined" />
          </Stack>
        </Box>
        <Button startIcon={<Edit />} variant="contained" onClick={() => onEdit(entry)} size="small" sx={{ borderRadius: "12px", boxShadow: "none" }}>
          Editar
        </Button>
      </Box>

      <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", mb: 4 }}>
        <Box>
          <Typography variant="caption" color="text.secondary">Fecha</Typography>
          <Typography fontWeight={600}>{new Date(entry.date).toLocaleDateString('es-ES')}</Typography>
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" color="text.secondary">Concepto</Typography>
          <Typography fontWeight={600}>{entry.concept}</Typography>
        </Box>
      </Box>

      <TableContainer sx={{ mb: 3, borderRadius: "12px", border: "1px solid", borderColor: theme.palette.divider }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)' }}>
              <TableCell sx={{ fontWeight: 600 }}>Cuenta</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Descripción</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Debe</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Haber</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {entry.lines?.map((line) => (
              <TableRow key={line.id}>
                <TableCell sx={{ fontFamily: "monospace" }}>{line.accountCode} - {line.accountName}</TableCell>
                <TableCell>{line.description}</TableCell>
                <TableCell align="right" sx={{ color: 'success.main', fontWeight: 500 }}>{line.debit > 0 ? line.debit.toFixed(2) : ''}</TableCell>
                <TableCell align="right" sx={{ color: 'error.main', fontWeight: 500 }}>{line.credit > 0 ? line.credit.toFixed(2) : ''}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Box sx={{ p: 2, borderRadius: "12px", border: "1px solid", borderColor: theme.palette.divider, minWidth: 250 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
            <Typography variant="body2">Total Debe</Typography>
            <Typography fontWeight={600} color="success.main">{totals.debit.toFixed(2)}€</Typography>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography variant="body2">Total Haber</Typography>
            <Typography fontWeight={600} color="error.main">{totals.credit.toFixed(2)}€</Typography>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default JournalEntryDetail;